import { Toast as CapacitorToast } from "@capacitor/toast";
import { Sonner } from "@disasterscience/scaffoldy";
import { AxiosError } from "axios";
import { ReactNode } from "react";
import { isCapacitor } from "./client";
import { asRateLimit, retryAfterText } from "./friendly";

type ToastContent = string | ReactNode;

export class Toast {
    static success(payload: ToastContent) {
        if (payload && isCapacitor()) {
            CapacitorToast.show({ text: payload.toString(), duration: "short" });
            return;
        }

        Sonner.success(payload);
    }

    static info(payload: ToastContent) {
        if (payload && isCapacitor()) {
            CapacitorToast.show({ text: payload.toString(), duration: "short" });
            return;
        }

        Sonner.info(payload);
    }

    static error(payload: ToastContent) {
        if (payload && isCapacitor()) {
            CapacitorToast.show({ text: payload.toString(), duration: "long" });
            return;
        }

        Sonner.error(payload);
    }

    static infoAction(payload: ToastContent, callback: CallableFunction, label = "Ok") {
        if (payload && isCapacitor()) {
            CapacitorToast.show({ text: payload.toString(), duration: "long" });
            return;
        }

        Sonner.info(payload, {
            action: {
                label,
                onClick: () => {
                    callback();
                },
            },
        });
    }

    static errorApi(payload: ToastContent, apiError: AxiosError) {
        console.log("errorApi", apiError);
        if (!payload) {
            payload = "";
        }

        const rateLimit = asRateLimit(apiError);
        if (rateLimit) {
            Toast.error(
                `${payload.toString()}: too many attempts, please try again ${retryAfterText(rateLimit)}.`
            );
            return;
        }

        // Extract text
        const text = payload.toString();
        let apiErrorText = "";

        // Extract the basic message, if it's present
        if (apiError && "message" in apiError) {
            apiErrorText = `Error: ${apiError.message}`;
        }

        // Extract error from API
        if (
            apiError &&
            "response" in apiError &&
            apiError.response &&
            "data" in apiError.response &&
            apiError.response.data &&
            typeof apiError.response.data === "object"
        ) {
            apiErrorText = `API Said: ${JSON.stringify(apiError.response.data)}`;
        } else if (
            apiError &&
            "response" in apiError &&
            apiError.response &&
            "data" in apiError.response &&
            apiError.response.data
        ) {
            apiErrorText = `API Said: ${apiError.response.data}`;
        }

        // Show as capacitor error
        if (payload && isCapacitor()) {
            CapacitorToast.show({
                text: `${text} ${apiErrorText}`,
                duration: "long",
            });
            return;
        }

        // Show with react toast
        Sonner.error(
            <div>
                {text}
                {apiErrorText ? (
                    <small>
                        <br />
                        {apiErrorText}
                    </small>
                ) : (
                    <></>
                )}
            </div>
        );
    }
}
